import React from 'react';
import PropTypes from 'prop-types';
import { Tabs } from 'antd';
import './ModalTabs.scss';

/**
 * Modal Tabs Component
 *
 * Integrated tabs component for modals with standardized styling.
 * Works seamlessly with Modal.Body for tabbed content.
 *
 * @example
 * <Modal.Body>
 *   <Modal.Tabs activeKey={activeTab} onChange={setActiveTab}>
 *     <Modal.Tabs.Tab key="tab1" label="First Tab">
 *       <Table ... />
 *     </Modal.Tabs.Tab>
 *     <Modal.Tabs.Tab key="tab2" label="Second Tab">
 *       <Table ... />
 *     </Modal.Tabs.Tab>
 *   </Modal.Tabs>
 * </Modal.Body>
 */
const ModalTabs = ({
    activeKey,
    onChange,
    children,
    className = '',
    type = 'line',
    size = 'default',
    ...props
}) => {
    // Transform Modal.Tabs.Tab children into Ant Design TabPane items
    const items = React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) return null;

        const { label, children: tabContent } = child.props;
        const tabKey = child.key;

        return {
            key: tabKey,
            label: label,
            children: (
                <div className="ds-modal-tabs__content">
                    {tabContent}
                </div>
            )
        };
    })?.filter(Boolean);

    return (
        <div className={`ds-modal-tabs ${className}`}>
            <Tabs
                activeKey={activeKey}
                onChange={onChange}
                type={type}
                size={size}
                className="ds-modal-tabs__container"
                items={items}
                animated={{ inkBar: true, tabPane: true }}
                {...props}
            >
            </Tabs>
        </div>
    );
};

/**
 * Modal Tab Item Component
 * Note: This is a lightweight wrapper consumed by ModalTabs above.
 * The 'key' prop should be passed directly on the Modal.Tabs.Tab element
 */
const ModalTabsTab = ({ children }) => <>{children}</>;

ModalTabs.Tab = ModalTabsTab;

ModalTabs.propTypes = {
    /** Active tab key */
    activeKey: PropTypes.string,

    /** Tab change handler */
    onChange: PropTypes.func,

    /** Tab items (Modal.Tabs.Tab components) */
    children: PropTypes.node,

    /** Additional CSS classes */
    className: PropTypes.string,

    /** Tab type */
    type: PropTypes.oneOf(['line', 'card']),

    /** Tab size */
    size: PropTypes.oneOf(['small', 'default', 'large'])
};

ModalTabsTab.propTypes = {
    /** Tab label */
    label: PropTypes.node.isRequired,

    /** Tab content */
    children: PropTypes.node,

    /** Tab key (inherited from parent) */
    key: PropTypes.string
};

export default ModalTabs;
